import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Button } from '@/shared/components/ui/Button';
import { useToast } from '@/shared/components/ui/Toast';
import {
  useCreateLocationManufacturerSettings,
  useUpdateLocationManufacturerSettings,
  useActiveManufacturers,
} from '../hooks/useAdmin';
import type { LocationManufacturerSettingsWithDetails } from '../services/locationManufacturerSettingsService';

const overrideSchema = z.object({
  manufacturer_id: z.string().min(1, 'Manufacturer is required'),
  min_order_qty: z.number().nullable(),
  min_order_amt: z.number().nullable(),
  free_ship_amt: z.number().nullable(),
  preferred_ordering_day: z.number().nullable(),
  notes: z.string().optional(),
});

type OverrideFormData = z.infer<typeof overrideSchema>;

interface ManufacturerOverrideFormProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  override?: LocationManufacturerSettingsWithDetails;
}

export function ManufacturerOverrideForm({
  isOpen,
  onClose,
  locationId,
  override,
}: ManufacturerOverrideFormProps) {
  const { showToast } = useToast();
  const isEditing = !!override;
  const { data: manufacturers } = useActiveManufacturers();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OverrideFormData>({
    resolver: zodResolver(overrideSchema),
  });

  const createMutation = useCreateLocationManufacturerSettings();
  const updateMutation = useUpdateLocationManufacturerSettings();

  useEffect(() => {
    if (isOpen) {
      if (override) {
        reset({
          manufacturer_id: override.manufacturer_id,
          min_order_qty: override.min_order_qty,
          min_order_amt: override.min_order_amt,
          free_ship_amt: override.free_ship_amt,
          preferred_ordering_day: override.preferred_ordering_day,
          notes: override.notes || '',
        });
      } else {
        reset({
          manufacturer_id: '',
          min_order_qty: null,
          min_order_amt: null,
          free_ship_amt: null,
          preferred_ordering_day: null,
          notes: '',
        });
      }
    }
  }, [isOpen, override, reset]);

  const onSubmit = async (data: OverrideFormData) => {
    try {
      if (isEditing && override) {
        await updateMutation.mutateAsync({
          id: override.id,
          min_order_qty: data.min_order_qty,
          min_order_amt: data.min_order_amt,
          free_ship_amt: data.free_ship_amt,
          preferred_ordering_day: data.preferred_ordering_day,
          notes: data.notes || null,
        });
        showToast('Manufacturer override updated successfully', 'success');
      } else {
        await createMutation.mutateAsync({
          location_id: locationId,
          manufacturer_id: data.manufacturer_id,
          min_order_qty: data.min_order_qty,
          min_order_amt: data.min_order_amt,
          free_ship_amt: data.free_ship_amt,
          preferred_ordering_day: data.preferred_ordering_day,
          notes: data.notes || null,
        });
        showToast('Manufacturer override created successfully', 'success');
      }
      onClose();
      reset();
    } catch (error) {
      console.error('Error saving manufacturer override:', error);
      showToast(
        error instanceof Error
          ? error.message
          : 'Failed to save manufacturer override',
        'error'
      );
    }
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        isEditing
          ? 'Edit Manufacturer Override'
          : 'Add Manufacturer Override'
      }
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Select
          label="Manufacturer"
          required
          disabled={isEditing}
          options={[
            { value: '', label: 'Select a manufacturer' },
            ...(manufacturers?.map((m) => ({ value: m.id, label: m.name })) ||
              []),
          ]}
          error={errors.manufacturer_id?.message}
          {...register('manufacturer_id')}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Min Order Qty"
            type="number"
            placeholder="Leave blank for default"
            error={errors.min_order_qty?.message}
            {...register('min_order_qty', { valueAsNumber: true })}
          />
          <Input
            label="Min Order Amount ($)"
            type="number"
            step="0.01"
            placeholder="Leave blank for default"
            error={errors.min_order_amt?.message}
            {...register('min_order_amt', { valueAsNumber: true })}
          />
          <Input
            label="Free Ship Amount ($)"
            type="number"
            step="0.01"
            placeholder="Leave blank for default"
            error={errors.free_ship_amt?.message}
            {...register('free_ship_amt', { valueAsNumber: true })}
          />
        </div>

        <Select
          label="Preferred Ordering Day"
          options={[
            { value: '', label: 'No preference' },
            { value: '0', label: 'Sunday' },
            { value: '1', label: 'Monday' },
            { value: '2', label: 'Tuesday' },
            { value: '3', label: 'Wednesday' },
            { value: '4', label: 'Thursday' },
            { value: '5', label: 'Friday' },
            { value: '6', label: 'Saturday' },
          ]}
          error={errors.preferred_ordering_day?.message}
          {...register('preferred_ordering_day', { valueAsNumber: true })}
        />

        <Textarea
          label="Notes"
          placeholder="Additional notes..."
          rows={3}
          error={errors.notes?.message}
          {...register('notes')}
        />

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
